import { useCallback, useEffect, useRef } from 'react'
import { useBlocker } from 'react-router-dom'
import type { Blocker, Location } from 'react-router-dom'

interface UnsavedChangesGuardOptions {
  isDirty: boolean
  isNavigationAllowed?: (nextLocation: Location) => boolean
}

export function useUnsavedChangesGuard({ isDirty, isNavigationAllowed }: UnsavedChangesGuardOptions) {
  const bypassNextRef = useRef(false)

  const blocker = useBlocker(({ nextLocation }) => {
    if (bypassNextRef.current) {
      bypassNextRef.current = false
      return false
    }
    return isDirty && !(isNavigationAllowed?.(nextLocation) ?? false)
  })

  const blockerRef = useRef<Blocker>(blocker)
  useEffect(() => {
    blockerRef.current = blocker
  }, [blocker])

  useEffect(() => {
    if (blocker.state === 'blocked' && !isDirty) blocker.proceed()
  }, [blocker, isDirty])

  useEffect(() => {
    if (!isDirty) return
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // Older Chromium and Safari only show the native prompt when returnValue is set.
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  const allowNextNavigation = useCallback(() => {
    bypassNextRef.current = true
  }, [])

  return {
    isBlocked: blocker.state === 'blocked',
    isBlockedNow: () => blockerRef.current.state === 'blocked',
    stay: () => blockerRef.current.reset?.(),
    leave: () => blockerRef.current.proceed?.(),
    allowNextNavigation,
  }
}
